/**
 * Keeping the match the player is looking at where it is on the screen: a
 * list's rows carry `data-row-fi`; before the list changes (another market
 * picked) or when the player leaves it, the first row on screen and its
 * distance from the top are noted, and after the change the page is moved so
 * that row stands there again (owner, 2026-11-05: "ako gleda ispred sebe tu
 * dole igru da ne skrolne ga gore nego da odmah tu ponudi").
 *
 * Done by hand: iPhone Safari has no CSS scroll anchoring, so a list that
 * gets shorter above the screen moves everything up there.
 */
export const ROW_ATTR = 'data-row-fi';

export type RowAnchor = {
  /** The row on screen. */
  fi: string;
  /** Its top, from the top of the window, in px. */
  top: number;
  /** The rows after it, in order: one of them stands in if it goes (a
   *  market only some matches have hides the others). */
  next: string[];
};

/** How many following rows are noted to stand in for the anchor. */
const NEXT_ROWS = 40;

/**
 * The row the player is looking at: the first whose top is at or below the
 * line (just under the bars that stay on top); when every row starts above
 * it, the last of them (a tall row filling the screen).
 */
export const pickAnchorIndex = (tops: number[], line: number): number => {
  if (tops.length === 0) return -1;
  const i = tops.findIndex((top) => top >= line);
  if (i >= 0) return i;
  return tops.length - 1;
};

/** The anchor itself if it is still listed, else the first row after it. */
export const firstPresent = (
  anchor: RowAnchor,
  has: (fi: string) => boolean
): string | null => {
  if (has(anchor.fi)) return anchor.fi;
  return anchor.next.find(has) ?? null;
};

const rowsOnPage = (): HTMLElement[] =>
  Array.from(document.querySelectorAll<HTMLElement>(`[${ROW_ATTR}]`));

const rowById = (fi: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[${ROW_ATTR}="${fi.replace(/["\\]/g, '\\$&')}"]`
  );

/** The page's fixed title bar: rows under it are not seen. */
export const topBarHeight = (): number => {
  const v = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--menu-height')
  );
  return Number.isFinite(v) ? v : 0;
};

/** The first row on screen below `line` (px from the window's top). */
export const captureRowAnchor = (line = topBarHeight()): RowAnchor | null => {
  if (typeof document === 'undefined') return null;
  const rows = rowsOnPage();
  // The rows below the screen do not count.
  const tops = rows.map((r) => r.getBoundingClientRect().top);
  const seen = tops.filter((top) => top < window.innerHeight);
  const i = pickAnchorIndex(seen, line);
  if (i < 0) return null;
  const fi = rows[i].getAttribute(ROW_ATTR);
  if (!fi) return null;
  return {
    fi,
    top: tops[i],
    next: rows
      .slice(i + 1, i + 1 + NEXT_ROWS)
      .map((r) => r.getAttribute(ROW_ATTR))
      .filter((x): x is string => !!x),
  };
};

/** The page's own scroller: the nearest ancestor that scrolls, else the
 *  window. */
const scrollerOf = (el: HTMLElement): HTMLElement | null => {
  let p: HTMLElement | null = el.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight)
      return p;
    p = p.parentElement;
  }
  return null;
};

/**
 * Moves the page so the anchor row (or the one after it that is still listed)
 * stands where it stood. 'missing' when none of them is on the page (yet),
 * 'short' when the page cannot scroll that far (yet), else 'placed'.
 */
export const restoreRowAnchor = (
  anchor: RowAnchor
): 'missing' | 'short' | 'placed' => {
  const fi = firstPresent(anchor, (x) => !!rowById(x));
  const el = fi ? rowById(fi) : null;
  if (!el) return 'missing';
  const by = Math.round(el.getBoundingClientRect().top - anchor.top);
  if (by === 0) return 'placed';
  const box = scrollerOf(el);
  // At once: a smooth scroll would show the list sliding.
  const instant = { top: by, behavior: 'instant' as ScrollBehavior };
  if (box) box.scrollBy(instant);
  else window.scrollBy(instant);
  const left = Math.round(el.getBoundingClientRect().top - anchor.top);
  return Math.abs(left) <= 1 ? 'placed' : 'short';
};

/** Where a list was left, for the way back to it (a match, the slip). */
export const POSITION_MAX_AGE_MS = 30 * 60 * 1000;

export const serializePosition = (anchor: RowAnchor, now: number) =>
  JSON.stringify({ ...anchor, at: now });

export const parsePosition = (
  raw: string | null,
  now: number,
  maxAgeMs = POSITION_MAX_AGE_MS
): RowAnchor | null => {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw);
    if (
      !p ||
      typeof p.fi !== 'string' ||
      typeof p.top !== 'number' ||
      typeof p.at !== 'number' ||
      now - p.at > maxAgeMs
    )
      return null;
    const next = Array.isArray(p.next)
      ? p.next.filter((x: unknown): x is string => typeof x === 'string')
      : [];
    return { fi: p.fi, top: p.top, next };
  } catch {
    return null;
  }
};
